import React, { useState, useEffect, useContext, useCallback, MouseEvent } from "react";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import { PDFViewerContext } from "contexts/PdfViewerContext";
import styles from "./PDFViewer.module.scss";
import Button from "@mui/material/Button";
import { AnimatePresence, motion } from "motion/react";
import { BrowserNativePdfViewer } from "./BrowserNativePdfViewer";
import { MemberPortalConfigContext } from "contexts/MemberPortalConfigContext";
import { SyncfusionPdfViewer } from "./SyncfusionPdfViewer";
import { accessToken$ } from "services/subjects";

export function PDFViewer() {
  const {
    dialogOpen,
    documents,
    setDialogOpen,
    removeDocument,
    currentDocument,
    setCurrentDocument,
  } = useContext(PDFViewerContext);
  const [value, setValue] = useState(0);

  const context = useContext(MemberPortalConfigContext);

  const handleChange = useCallback(
    (_event: React.SyntheticEvent, newValue: number) => {
      setCurrentDocument(documents[newValue]?.documentPath || '');
    },
    [documents]
  );

  const handleClickMyDocuments = useCallback(() => {
    setDialogOpen(true);
  }, []);

  const handleClickCloseDocument = useCallback((event: React.MouseEvent<SVGSVGElement>, documentPath: string) => {
    event.stopPropagation();
    removeDocument(documentPath);
  }, [documents, removeDocument]);

  function handleClickCloseViewer() {
    setDialogOpen(false);
  }

  const [accessToken, setAccessToken] = useState('');

  useEffect(() => {
    const subscription = accessToken$.subscribe({
      next: (token) => setAccessToken(token),
    });
    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (documents.length === 0) {
      setValue(0);
      if (currentDocument) {
        setCurrentDocument("");
      }
      return;
    }

    const index = documents.findIndex(
      (document) => document.documentPath === currentDocument
    );

    if (index >= 0) {
      setValue(index);
    } else {
      const nextValue = Math.min(value, documents.length - 1);
      setValue(nextValue);
      setCurrentDocument(documents[nextValue]?.documentPath ?? "");
    }
  }, [documents, currentDocument, value, setCurrentDocument]);

  const safeValue =
    documents.length === 0 ? false : Math.min(Math.max(value, 0), documents.length - 1);

  return (
    <div className={styles.PDFViewer}>
      <div className={styles["floating-btn-container"]}>
        {!!documents.length && (
          <Button
            variant="contained"
            className={styles["floating-btn"]}
            onClick={handleClickMyDocuments}
          >
            <span>My Documents</span>
            <span className={styles["icon-container"]}>
              <DescriptionIcon fontSize="large" />
              <span className={styles["badge"]}>{documents.length}</span>
            </span>
          </Button>
        )}
      </div>
      <>
        <AnimatePresence>
          {dialogOpen && (
            <motion.article
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            >
              <article
                onClick={handleClickCloseViewer}
                className={styles["dialog-background"]}
              />
            </motion.article>
          )}
        </AnimatePresence>
        <article
          className={`${styles["dialog-container"]} ${dialogOpen && styles.open
            }}`}
        >
          <article className={`${styles.dialog} ${dialogOpen && styles.open}`}>
            <article>
              <Tabs
                value={safeValue}
                onChange={handleChange}
                variant="scrollable"
                scrollButtons="auto"
              >
                {documents.map((document, index) => (
                  <Tab
                    className={`${styles.tab} ${safeValue === index && styles.active
                      }`}
                    key={document.documentPath}
                    label={
                      <>
                        <span className={styles.text}>
                          {document.displayName}
                        </span>
                        <CloseIcon
                          className={styles.close}
                          onClick={(event) => handleClickCloseDocument(event, document.documentPath)}
                        />
                      </>
                    }
                  />
                ))}
              </Tabs>
            </article>
            <article className={styles["dialog-body"]}>
              {context.memberPortalConfig?.useNativePDFViewerFlag ? 
                <BrowserNativePdfViewer
                  documentId={documents[value]?.documentPath}
                  serviceUrl={documents[value]?.serviceUrl}
                /> : documents.map((document, index) => (
                <SyncfusionPdfViewer
                  accessToken={accessToken}
                  key={document.documentPath}
                  document={document}
                />
              )
              )}
            </article>
          </article>
        </article>
      </>
    </div>
  );
}
